import { useFrame } from '@react-three/fiber'
import { type ReactNode, useEffect, useRef } from 'react'
import { useRunState } from '../../provider/useRunState'
import { store } from '../../../stores/store'
import { trackEndDecelerationDuration } from '../../config/constants'
import type { TrackController } from './trackController'
import { TrackControllerContext } from './trackControllerState'

export function TrackControllerProvider({ controller, children }: {
    controller: TrackController
    children: ReactNode
}) {
    return (
        <TrackControllerContext.Provider value={controller}>
            <TrackControllerFrameLoop controller={controller} />
            <TrackProgressSync controller={controller} />
            {children}
        </TrackControllerContext.Provider>
    )
}
function isLevelCompleteSequencePhase(phase: ReturnType<typeof useRunState>['phase']) {
    return phase === 'levelComplete'
}

function easeOutQuad(t: number) {
    return 1 - (1 - t) * (1 - t)
}

function TrackControllerFrameLoop({ controller }: {
    controller: TrackController
}) {
    const runState = useRunState()
    const previousPhaseRef = useRef(runState.phase)

    useFrame((_, deltaSeconds) => {
        const safeDeltaSeconds = Math.min(deltaSeconds, 0.05)
        const previousPhase = previousPhaseRef.current
        const currentPhase = runState.phase

        if (
            isLevelCompleteSequencePhase(currentPhase) &&
            !isLevelCompleteSequencePhase(previousPhase)
        ) {
            controller.beginEndDeceleration()
        }

        previousPhaseRef.current = currentPhase

        if (isLevelCompleteSequencePhase(currentPhase)) {
            const nextLevelCompleteTimer =
                runState.snapshot.levelCompleteTimer + safeDeltaSeconds
            runState.setLevelCompleteTimer(nextLevelCompleteTimer)

            const decelerationProgress =
                trackEndDecelerationDuration > 0
                    ? Math.min(
                        nextLevelCompleteTimer / trackEndDecelerationDuration,
                        1,
                    )
                    : 1
            const speedMultiplier = 1 - easeOutQuad(decelerationProgress)
            controller.setSpeedMultiplier(Math.max(speedMultiplier, 0))

            if (decelerationProgress >= 1) {
                return
            }

            controller.updateTrackPosition(safeDeltaSeconds)
            return
        }

        if (currentPhase === 'running' && previousPhase !== 'running') {
            controller.setSpeedMultiplier(1)
            controller.clearEndDeceleration()
        }

        if (runState.phase !== 'running') {
            return
        }

        if (runState.snapshot.hitFreezeTimer > 0) {
            return
        }

        const trackLength = controller.getTrackLength()
        const playbackState = controller.getTrackPlaybackState()
        const nextTrackPosition =
            playbackState.trackPosition + safeDeltaSeconds * playbackState.speed

        if (trackLength > 0 && nextTrackPosition >= trackLength) {
            controller.setTrackPosition(trackLength)
            controller.beginEndDeceleration()
            runState.completeRun()
            return
        }

        controller.updateTrackPosition(safeDeltaSeconds)
    })

    return null
}

function TrackProgressSync({ controller }: {
    controller: TrackController
}) {
    const runState = useRunState()

    useEffect(() => {
        const updateTrackPlayback = () => {
            const trackLength = controller.getTrackLength()
            const trackPosition = controller.getTrackPlaybackState().trackPosition

            store.setTrackDistance(trackPosition)
            store.setTrackProgress(
                trackLength > 0
                    ? Math.min(trackPosition / trackLength, 1)
                    : 0,
            )
            runState.setTrackRegionState(
                controller.getTrackRegionState(trackPosition),
            )
        }

        updateTrackPlayback()
        return controller.subscribeTrackPlayback(() => {
            updateTrackPlayback()
        })
    }, [controller, runState])

    return null
}
